import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import searchIcon from "../../assets/search_icon.png";
import withdrawIcon from "../../assets/debt_icon.png";
import depositIcon from "../../assets/payment_icon.png";
import CustomButton from "../../components/CustomButton";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import { toMoney } from "../../utils/toMoney";
import { formatDate } from "../../utils/toDate";
import TextDiv from "../../components/TextDiv";
import Modal from "../../components/Modal";
import BalanceBoxes from "../../components/BalanceBoxes";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { UserAuth } from "../../utils/AuthContext";
import CustomInput from "../../components/CustomInput";
import SearchFilter from "../../components/SearchFilter";
import InformationBox from "../../components/InformationBox";
import BackToDashboard from "../../components/BackToDashboard";
import useLanguage from "../../utils/useLanguage";

// Import your kassa service functions here
import {
  getKassaById,
  createKassaTransaction,
  updateKassaTransaction,
  deleteKassa,
  deleteKassaTransaction,
  // deleteKassaTransaction,
} from "../../services/kassa/kassaServices";

import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import closeIcon from "../../assets/close_icon.png";
import saveIcon from "../../assets/save_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import downloadIcon from "../../assets/download-icon.svg";
import infoIcon from "../../assets/information.png";

import { pdf } from "@react-pdf/renderer";

// Import modals for withdraw and deposit
import KassaWithdrawModal from "./KassaWithdrawlModal";
import KassaDepositModal from "./KassaDepositeModal";
import Dropdown from "../../components/Dropdown";
import KassaTransactionPdfTemplate from "../../components/KassaTransactionsTemplate";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import AreYouSureModal from "../../components/AreYouSureModal";
import { updateStudentPayment } from "../../services/StudentServices";
import ErrorBox from "../../components/ErrorBox";

const KassaPage = () => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const { id: kassaId } = useParams();

  // STATES - Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // STATES - Modals
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [depositModal, setDepositModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [editFormData, setEditFormData] = useState({
    amount: "",
    date: "",
    notes: "",
    transactionType: "",
  });

  // STATES - Search (kept for UI but not used for filtering)
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("Date"),
    value: "date",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [deleteKassaTransactionError, setDeleteKassaTransactionError] =
    useState("");
  const [updateKassaTransactionError, setUpdateKassaTransactionError] =
    useState("");

  // STATES - Data
  const [kassaTransactions, setKassaTransactions] = useState([]);
  const [kassaInfo, setKassaInfo] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isUpdatingKassa, setIsUpdatingKassa] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // CONSTANTS
  const searchTitles = [
    { title: t("Date"), value: "date" },
    { title: t("amount"), value: "amount" },
    { title: t("transactionType"), value: "transactionType" },
  ];

  const tableTitles = [
    { value: "transactionType", keepOnMobile: true },
    { value: "receiptNumber", keepOnMobile: true },
    { value: "amount", keepOnMobile: true },
    { value: "Date", keepOnMobile: true },
    { value: "info" },
  ];

  // HANDLERS
  const handleDownloadKassaTransactionsPDF = async () => {
    setPdfLoading(true);

    try {
      const filterParams = {
        page: 1,
        limit: 100000,
        startDate,
        endDate,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
        searchQuery,
        searchField: selectedTitle?.value,
        type: transactionType.value || undefined,
      };

      const response = await getKassaById(kassaId, filterParams);

      if (response?.data?.transactions) {
        const kassa = response?.data?.kassa || {};
        // Generate the PDF with your custom component
        const blob = await pdf(
          <KassaTransactionPdfTemplate
            language={currentDirection === "rtl" ? "AR" : "EN"}
            companyName={
              userData?.subCompany?.name ||
              localStorage.getItem("organizationName") ||
              ""
            }
            data={{
              kassa,
              transactions: response.data.transactions,
            }}
            transactions={response.data.transactions.map((trx) => ({
              ...trx,
              type: trx.type,
              name: trx.description || trx.type,
            }))}
            schoolName={kassa.organization?.organizationName}
          />
        ).toBlob();

        // Trigger browser download
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${
          response.data?.kassa?.name || "kassa-transactions"
        }-${new Date().getMonth() + 1}-${new Date().getDate()}-${(
          Math.random() * 1000
        ).toFixed(0)}.pdf`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSearchType = (type) => {
    setSelectedTitle(type);
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
    setTransactionType(""); // Add this line
  };

  const getAllKassaTransactions = async (page = 1, limit = 10) => {
    setLoading(true);

    try {
      const filterParams = {
        page,
        limit,
        startDate,
        endDate,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
        searchQuery,
        searchField: selectedTitle?.value,
        type: transactionType.value || undefined, // Add this line
      };

      const response = await getKassaById(kassaId, filterParams);

      if (response.status === 200) {
        const kassaData = response.data;

        // Set kassa info
        setKassaInfo({
          id: kassaData?.kassa.id,
          name: kassaData?.kassa.name,
          organizationId: kassaData?.kassa.organizationId,
        });

        // Calculate balance from all transactions (not filtered ones)

        setBalance(kassaData.balance);

        // Use pagination data from backend response
        setPagination({
          currentPage: kassaData.currentPage || page,
          totalPages: kassaData.totalPages || 1,
          totalRecords: kassaData.totalRecords || 0,
        });

        // Transform data for table display (use filtered/paginated data from backend)
        const transactions = kassaData.transactions || [];

        setKassaTransactions(transactions);
      }
    } catch (error) {
      console.error("Failed to fetch kassa data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get transaction type display name
  const getTransactionTypeDisplay = (type) => {
    switch (type) {
      case "studentDept":
        return t("installmentPayment");
      case "deposit":
      case "DIRECTDEPOSIT":
        return t("deposit");
      case "withdraw":
      case "DIRECTWITHDRAWAL":
        return t("withdrawal");
      case "PURCHASE":
        return t("PURCHASE".toLowerCase());
      case "reverseTransaction":
        return t("returnMoney");
      case "SELL":
        return t("SELL");
      case "PAYSLIP":
        return t("payslip");
      default:
        return type || t("unknown");
    }
  };

  // Helper function to determine transaction sign for styling
  const getTransactionSign = (type) => {
    // Positive amounts (deposits/income)
    if (
      type === "deposit" ||
      type === "studentDept" ||
      type === "DIRECTDEPOSIT" ||
      type === "SELL"
    ) {
      return "positive-value";
    }
    // Negative amounts (withdrawals/expenses)
    return "negative-value";
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    getAllKassaTransactions(1);
  };

  const handlePaginationChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    getAllKassaTransactions(newPage);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
    setTransactionType("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    getAllKassaTransactions(1);
  };

  const handleEditClick = () => {
    setEditFormData({
      amount: parseFloat(selectedTransaction?.amount) || "",
      date: selectedTransaction?.date
        ? selectedTransaction.date.split("T")[0]
        : "",
      notes: selectedTransaction?.notes || "",
      transactionType: selectedTransaction?.type || "",
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      amount: "",
      date: "",
      notes: "",
      transactionType: "",
    });
  };

  const handleSaveEdit = async () => {
    try {
      setIsUpdatingKassa(true);
      const response = await updateKassaTransaction(selectedTransaction.id, {
        ...editFormData,
        amount: parseFloat(editFormData.amount),
      });

      if (response.status === 200) {
        setDetailsModal(false);
        setIsEditing(false);
        getAllKassaTransactions(pagination.currentPage);
      }
    } catch (error) {
      if (
        error.response.data.error === "KassaTransaction type is not updatable"
      ) {
        setUpdateKassaTransactionError("kassaTransactionTypeIsNonUpdatable");
      }
      console.error("Error updating transaction:", error);
    } finally {
      setIsUpdatingKassa(false);
    }
  };

  const handleUpdateStudentPayment = async () => {
    try {
      setIsUpdatingKassa(true);
      const response = await updateStudentPayment(
        selectedTransaction.studentPaymentId,
        {
          ...editFormData,
          amount: parseFloat(editFormData.amount),
          type: selectedTransaction.type,
        }
      );

      if (response.status === 200) {
        setDetailsModal(false);
        setIsEditing(false);
        getAllKassaTransactions(pagination.currentPage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdatingKassa(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      const response = await deleteKassaTransaction(selectedTransaction.id);

      if (response.status === 200) {
        setDetailsModal(false);
        setShowDeleteConfirmation(false);
        getAllKassaTransactions(pagination.currentPage);
      }
    } catch (error) {
      console.log(error);
      if (error.code === "ERR_BAD_REQUEST") {
        setDeleteKassaTransactionError(
          t("kassaTransactionTypeNotDeletableError")
        );
        return;
      }
      console.error("Error deleting transaction:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderSearchInput = () => {
    switch (selectedTitle.value) {
      case "date":
        return (
          <div className="o-column-row-switcher o-column--gap-size_300">
            <CustomInput
              name="startDate"
              title={t("from")}
              type="date"
              thick
              onChange={(e) => setStartDate(e.target.value)}
              resetSearch={() => setStartDate("")}
              value={startDate}
            />
            <CustomInput
              name="endDate"
              title={t("to")}
              type="date"
              thick
              onChange={(e) => setEndDate(e.target.value)}
              resetSearch={() => setEndDate("")}
              value={endDate}
            />
          </div>
        );

      case "transactionType":
        return (
          <div className="o-column">
            <label>{t("transactionType")}</label>
            <Dropdown
            selected={transactionType}
              options={[
                { title: t("withdrawal"), value: "DIRECTWITHDRAWAL" },
                { title: t("deposit"), value: "DIRECTDEPOSIT" },
                { title: t("installmentPayment"), value: "studentDept" },
                { title: t("purchase"), value: "PURCHASE" },
                { title: t("returnMoney"), value: "reverseTransaction" },
                { title: t("payslip"), value: "PAYSLIP" },
                { title: t("selectAll"), value: "" },
              ]}
              setSelected={(transactionType) => {
                setTransactionType(transactionType);
              }}
              onSelect={(option) => setTransactionType(option.value)}
              placeholder={t("selectTransactionType")}
              thick
              flex
            />
          </div>
        );

      case "amount":
        return (
          <div className="o-column-row-switcher o-column--gap-size_300">
            <CustomInput
              name="minAmount"
              title={t("minAmount")}
              type="number"
              placeholder={t("minAmount")}
              thick
              onChange={(e) => setMinAmount(e.target.value)}
              resetSearch={() => setMinAmount("")}
              value={minAmount}
              flex
            />
            <CustomInput
              name="maxAmount"
              title={t("maxAmount")}
              type="number"
              placeholder={t("maxAmount")}
              thick
              onChange={(e) => setMaxAmount(e.target.value)}
              resetSearch={() => setMaxAmount("")}
              value={maxAmount}
              flex
            />
          </div>
        );

      default:
        return (
          <CustomInput
            placeholder={`${t("searchTitle")} ${t(selectedTitle?.value)}`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            resetSearch={handleClearSearch}
            thick
            flex
          />
        );
    }
  };

  useEffect(() => {
    if (kassaId) {
      getAllKassaTransactions(pagination.currentPage);
    }
  }, [kassaId, pagination.currentPage, transactionType.value]);

  const tableRows = kassaTransactions?.map((transaction) => [
    {
      value: getTransactionTypeDisplay(transaction.type),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: transaction.receiptNumber,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: transaction.amount,
      type: TABLE_CELL_TYPES.MONEY,
      sign: getTransactionSign(transaction.type),
      bold: true,
      keepOnMobile: true,
      currency: t("IQD"),
    },
    {
      value: transaction.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: (
        <div className="cell">
          <CustomButton
            variant="blank"
            onClick={() => {
              setSelectedTransaction({
                ...transaction,
                transactionTypeName: getTransactionTypeDisplay(
                  transaction.type
                ),
              });
              setDetailsModal(true);
            }}
            title={t("info")}
            icon={infoIcon}
            iconSize={20}
          />
        </div>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
    },
  ]);

  return (
    <>
      <main className="e-container">
        {/* Back to Dashboard */}
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
          />
        </div>

        <div className="invoice-table-container">
          {/* User Section */}
          <div className="user-section">
            <div className="user">
              <span className="user-text">{`${t("user")}:`}</span>
              <span className="user-text">
                {localStorage.getItem("username")}
              </span>
            </div>
          </div>

          {/* Page Title and Action Buttons */}
          <section className="o-column u-padding--top_200 ">
            <h2 className="t-heading_large u-margin_0 u-padding_0">
              {t("kassaPage")}
            </h2>
            {kassaInfo && (
              <p className="t-text-regular u-margin_0 u-margin--top_200 u-padding_0">
                {t("kassaName")}: <strong>{kassaInfo.name}</strong>
              </p>
            )}
          </section>

          <div className="o-row o-row--horizontal-align_end u-margin--bottom_300">
            {userData?.userType === "orgUser" && (
              <div className="o-row o-row--gap-size_300">
                <CustomButton
                  variant="red"
                  title={t("withdrawal")}
                  onClick={() => setWithdrawModal(true)}
                  icon={withdrawIcon}
                  iconSize={22}
                />
                <CustomButton
                  variant="green"
                  title={t("deposit")}
                  onClick={() => setDepositModal(true)}
                  icon={depositIcon}
                  iconSize={22}
                />
              </div>
            )}
          </div>

          {/* Search Section */}
          <div className="o-column o-column--gap-size_300">
            <label className="t-bold_semi-bold">
              {`${t("searchTitle")} ${t(selectedTitle?.title)}`}
            </label>
            <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_500">
              <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 o-column--horizontal-align_start">
                {renderSearchInput()}
                <SearchFilter
                  options={searchTitles}
                  selected={selectedTitle}
                  setSelected={handleSearchType}
                  thick
                />
              </div>

              <CustomButton
                title={t("search")}
                onClick={handleSearch}
                icon={searchIcon}
                iconSize={15}
                classList="c-search-button"
              />
            </section>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {kassaTransactions?.length ? (
                <section>
                  <RoundedTable
                    titles={tableTitles}
                    rows={tableRows}
                    pagination={pagination}
                    setPagination={setPagination}
                    onPaginationChange={handlePaginationChange}
                  />
                  {pdfLoading && <Loader />}
                  <div className="o-row o-row--horizontal-align_end u-margin--top_500 u-margin--bottom_400">
                    <CustomButton
                      variant="blank"
                      onClick={handleDownloadKassaTransactionsPDF}
                      disabled={pdfLoading}
                      icon={downloadIcon}
                      title={
                        pdfLoading ? t("generatingPdf") : t("downloadStatement")
                      }
                    />
                  </div>
                </section>
              ) : (
                <InformationBox>{t("noTransactions")}</InformationBox>
              )}
            </>
          )}

          {/* Balance Box */}
          <BalanceBoxes
            boxes={[
              {
                title: "balance",
                value: balance,
                currency: t("IQD"),
              },
            ]}
          />

          {/* Modals */}
          {withdrawModal && (
            <KassaWithdrawModal
              kassaId={kassaId}
              handleToggleModal={() => setWithdrawModal(false)}
              refetchData={() =>
                getAllKassaTransactions(pagination.currentPage)
              }
              currentBalance={balance}
            />
          )}

          {depositModal && (
            <KassaDepositModal
              kassaId={kassaId}
              handleToggleModal={() => setDepositModal(false)}
              refetchData={() =>
                getAllKassaTransactions(pagination.currentPage)
              }
            />
          )}

          {detailsModal && (
            <Modal
              modalSize="c-modal--small"
              title="info"
              onClose={() => {
                setDetailsModal(false);
                setIsEditing(false);
                setUpdateKassaTransactionError(null);
                setDeleteKassaTransactionError(null);
              }}
              error={t(updateKassaTransactionError)}
              footer={
                <>
                  {isUpdatingKassa && <Loader size={LOADER_SIZES.MEDIUM} />}
                  <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
                    {isEditing ? (
                      <>
                        <CustomButton
                          variant="blank"
                          title={t("cancel")}
                          onClick={handleCancelEdit}
                          icon={closeIcon}
                          iconSize={14}
                        />
                        <CustomButton
                          title={t("save")}
                          onClick={() => {
                            selectedTransaction.type === "studentDept" ||
                            selectedTransaction.type === "reverseTransaction"
                              ? handleUpdateStudentPayment()
                              : handleSaveEdit();
                          }}
                          disabled={loading}
                          icon={saveIcon}
                          iconSize={14}
                        />
                      </>
                    ) : (
                      <>
                        <CustomButton
                          variant="blank"
                          title={t("edit")}
                          onClick={handleEditClick}
                          icon={editIcon}
                          iconSize={18}
                        />
                        <CustomButton
                          variant="red"
                          title={t("delete")}
                          onClick={() => setShowDeleteConfirmation(true)}
                          icon={deleteIcon}
                          iconSize={16}
                        />
                      </>
                    )}
                  </div>
                </>
              }
            >
              {updateKassaTransactionError && (
                <ErrorBox>{t(updateKassaTransactionError)}</ErrorBox>
              )}
              <div className="o-column o-column--gap-size_300">
                <TextDiv
                  title="transactionType"
                  value={selectedTransaction?.transactionTypeName}
                  divClasses="o-row o-row--vertical-align_center o-row--gap-size_500"
                  pClasses="t-bold_semi-bold u-width-specific_80 u-margin_0"
                />

                <TextDiv
                  title="receiptNumber"
                  value={selectedTransaction?.receiptNumber}
                  divClasses="o-row o-row--vertical-align_center o-row--gap-size_500"
                  pClasses="t-bold_semi-bold u-width-specific_80 u-margin_0"
                />

                {isEditing ? (
                  <>
                    <div className="o-row o-row--vertical-align_center o-row--gap-size_500">
                      <p className="t-bold_semi-bold u-width-specific_80 u-margin_0">
                        {t("amount")}:
                      </p>
                      <CustomInputWithUnit
                        type="moneyV2"
                        value={editFormData.amount}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        unit={"IQD"}
                        thick
                        flex
                      />
                    </div>

                    <div className="o-row o-row--vertical-align_center o-row--gap-size_500">
                      <p className="t-bold_semi-bold u-width-specific_80 u-margin_0">
                        {t("Date")}:
                      </p>
                      <CustomInput
                        type="date"
                        value={editFormData.date}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            date: e.target.value,
                          }))
                        }
                        thick
                        flex
                      />
                    </div>

                    <div className="o-row o-row--vertical-align_start o-row--gap-size_500">
                      <p className="t-bold_semi-bold u-width-specific_80 u-margin_0">
                        {t("notes")}:
                      </p>
                      <CustomInput
                        type="textarea"
                        value={editFormData.notes}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder={t("notes")}
                        thick
                        flex
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <TextDiv
                      title="amount"
                      value={`${toMoney(selectedTransaction?.amount)} ${t(
                        "IQD"
                      )}`}
                      divClasses="o-row o-row--vertical-align_center o-row--gap-size_500"
                      pClasses="t-bold_semi-bold u-width-specific_80 u-margin_0"
                    />
                    <TextDiv
                      title="Date"
                      value={formatDate(selectedTransaction?.date)}
                      divClasses="o-row o-row--vertical-align_center o-row--gap-size_500"
                      pClasses="t-bold_semi-bold u-width-specific_80 u-margin_0"
                    />
                    <TextDiv
                      title="notes"
                      value={selectedTransaction?.notes || t("noNotes")}
                      divClasses="o-row o-row--vertical-align_start o-row--gap-size_500"
                      pClasses="t-bold_semi-bold u-width-specific_80 u-margin_0"
                      spanClasses="break-word"
                    />
                  </>
                )}
              </div>
            </Modal>
          )}

          {showDeleteConfirmation && (
            <AreYouSureModal
              onClose={() => {
                setShowDeleteConfirmation(false);
                setDeleteKassaTransactionError("");
              }}
              onConfirm={handleDeleteTransaction}
              customMessage="deleteTransactionConfirmation"
              closeTitle="cancel"
              confirmTitle="delete"
              error={deleteKassaTransactionError}
            />
          )}
        </div>
      </main>
    </>
  );
};

export default KassaPage;
